import { useCallback, useEffect, useRef, useState } from 'react';
import { useGameRoom } from './hooks/useGameRoom';
import { useChat } from './hooks/useChat';
import { useAuth } from './contexts/AuthContext';
import { useSocketEvent } from './hooks/useSocketEvent';
import { EVENTS } from '@flip7/shared';
import type { ShinyUnlockPayload } from '@flip7/shared';
import { ConfirmModal } from './components/ConfirmModal';
import { ConnectionBanner } from './components/ConnectionBanner';
import { MuteToggle } from './components/MuteToggle';
import { ChatPanel } from './components/ChatPanel';
import { Drawer } from './components/Drawer';
import { ScreenTransition } from './components/ScreenTransition';
import { HomeScreen } from './screens/HomeScreen';
import { TVModeScreen } from './screens/TVModeScreen';
import { StoryScreen } from './screens/StoryScreen';
import { DailyChallengeScreen } from './screens/DailyChallengeScreen';
import { ProfileScreen } from './screens/ProfileScreen';
import { ShopScreen } from './screens/ShopScreen';
import { ShinyCollectionScreen } from './screens/ShinyCollectionScreen';
import { DuelPubScreen } from './screens/DuelPubScreen';
import { ShinyUnlockToast } from './components/ShinyUnlockToast';
import { SocialPanel } from './screens/SocialPanel';
import { ReactionsPanel } from './components/ReactionsPanel';
import { AchievementsPanel } from './components/AchievementsPanel';
import { LobbyScreen } from './screens/LobbyScreen';
import { GameStartScreen } from './screens/GameStartScreen';
import { GameBoardScreen } from './screens/GameBoardScreen';
import { RoundSummaryScreen } from './screens/RoundSummaryScreen';
import { GameOverScreen } from './screens/GameOverScreen';
import { useReactions } from './hooks/useReactions';
import { HotStreakGameScreen } from './screens/HotStreakGameScreen';
import { HotStreakLobbyScreen } from './screens/HotStreakLobbyScreen';

export default function App() {
  const {
    gameState,
    roomCode,
    playerId,
    spectatorId,
    error,
    sessionReplaced,
    createRoom,
    joinRoom,
    spectateRoom,
    joinGroup,
    reconnect,
    startGame,
    addCard,
    removeCard,
    stay,
    bust,
    nextPlayer,
    undo,
    endRound,
    nextRound,
    addPlayer,
    removePlayer,
    hit,
    chooseTarget,
    playAgain,
    leaveRoom,
    toggleWantToPlay,
    kickSpectator,
    selectSwapCards,
    selectStealTarget,
    selectDiscardTarget,
    forfeitAction,
    startChallenge,
    challengeResult,
    startDailyChallenge,
    dailyResult,
  } = useGameRoom();

  const { user, refreshUser } = useAuth();
  const [showStoryMode, setShowStoryMode] = useState(false);
  const [showDailyChallenge, setShowDailyChallenge] = useState(false);
  const [showShop, setShowShop] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showCollection, setShowCollection] = useState(false);
  const [showDuelPub, setShowDuelPub] = useState(false);
  const [shinyUnlock, setShinyUnlock] = useState<{ cardKey: string; tier: string } | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [socialBadge, setSocialBadge] = useState(0);
  const [drawerTab, setDrawerTab] = useState<'reactions' | 'achievements' | 'friends'>('reactions');

  const [leaveConfirmOpen, setLeaveConfirmOpen] = useState(false);
  const [tvMode, setTvMode] = useState(false);

  const reactions = useReactions(roomCode);
  const chat = useChat(roomCode);

  // Browser history: back button support
  const gameStateRef = useRef(gameState);
  gameStateRef.current = gameState;

  useEffect(() => {
    const handlePopState = () => {
      const state = history.state;
      if (gameStateRef.current) {
        // During a game, back button triggers leave confirmation
        window.history.pushState(null, '');
        setLeaveConfirmOpen(true);
        return;
      }
      // Pre-game: navigate based on history state
      if (!state || state.screen === 'home') {
        setShowStoryMode(false);
        setShowDailyChallenge(false);
        setShowShop(false);
        setShowProfile(false);
        setShowCollection(false);
        setShowDuelPub(false);
      }
    };
    window.addEventListener('popstate', handlePopState);
    if (!window.history.state) {
      window.history.replaceState({ screen: 'home' }, '');
    }
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Extract room code or group slug from URL for shareable links
  const urlRoomCode = typeof window !== 'undefined'
    ? window.location.pathname.match(/\/room\/([A-Z0-9-]+)/)?.[1]
    : undefined;
  const urlGroupSlug = typeof window !== 'undefined'
    ? window.location.pathname.match(/\/group\/([a-z0-9][a-z0-9-]*[a-z0-9])/)?.[1]
    : undefined;

  const isHost = gameState?.players.find(p => p.id === playerId)?.isHost ?? false;
  const isSpectator = !!spectatorId;

  const handleLeave = useCallback(() => {
    setLeaveConfirmOpen(true);
  }, []);

  const confirmLeave = useCallback(() => {
    setLeaveConfirmOpen(false);
    leaveRoom();
  }, [leaveRoom]);

  // Track game start ceremony
  const [showCeremony, setShowCeremony] = useState(false);
  const prevStatusRef = useRef(gameState?.status);

  useEffect(() => {
    const prev = prevStatusRef.current;
    const curr = gameState?.status;

    // Show ceremony when transitioning from waiting to playing (round 1 only)
    if (prev === 'waiting' && curr === 'playing' && gameState?.currentRound === 1) {
      setShowCeremony(true);
    }

    prevStatusRef.current = curr;
  }, [gameState?.status, gameState?.currentRound]);

  // Update browser URL to reflect room/group
  const currentRoomCode = gameState?.roomCode;
  const currentGroupSlug = gameState?.groupSlug;
  useEffect(() => {
    if (!currentRoomCode) return;
    const expectedPath = currentGroupSlug ? `/group/${currentGroupSlug}` : `/room/${currentRoomCode}`;
    if (window.location.pathname !== expectedPath) {
      window.history.replaceState(null, '', expectedPath);
    }
  }, [currentRoomCode, currentGroupSlug]);

  // Warn before leaving an active game
  useEffect(() => {
    if (!gameState || gameState.status === 'finished') return;
    function onBeforeUnload(e: BeforeUnloadEvent) {
      e.preventDefault();
    }
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [gameState]);

  // Track social notification badge
  useSocketEvent(EVENTS.GAME_INVITE, () => { setSocialBadge(prev => prev + 1); });
  useSocketEvent(EVENTS.GROUP_INVITE, () => { setSocialBadge(prev => prev + 1); });
  useSocketEvent(EVENTS.FRIEND_REQUEST, () => { setSocialBadge(prev => prev + 1); });

  // Shiny card unlock notification
  useSocketEvent<ShinyUnlockPayload>(EVENTS.SHINY_UNLOCK, (data) => {
    if (data.playerId === playerId) {
      setShinyUnlock({ cardKey: data.cardKey, tier: data.tier });
      refreshUser();
    }
  });

  const openFriends = useCallback(() => { setDrawerTab('friends'); setDrawerOpen(true); setSocialBadge(0); }, []);
  const openDrawer = useCallback(() => { setDrawerTab('reactions'); setDrawerOpen(true); }, []);
  // Drawer trigger button (shown when logged in)
  const drawerTrigger = user ? (
    <button
      className="profile-fab"
      onClick={socialBadge > 0 ? openFriends : openDrawer}
      style={{ fontSize: user.activeAvatar ? '1.2rem' : '0.75rem' }}
      aria-label="Open drawer"
    >
      {user.activeAvatar || user.name.charAt(0).toUpperCase()}
      {socialBadge > 0 && (
        <span className="profile-fab__badge">
          {socialBadge > 9 ? '9+' : socialBadge}
        </span>
      )}
    </button>
  ) : null;

  // Drawer content
  const drawerContent = (
    <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)}>
      {/* Tab switcher */}
      <div className="drawer-v2__tabs">
        {([
          { key: 'reactions' as const, label: 'Reactions' },
          { key: 'achievements' as const, label: 'Achievements' },
          { key: 'friends' as const, label: socialBadge > 0 ? `Friends (${socialBadge})` : 'Friends' },
        ]).map(t => (
          <button
            key={t.key}
            className={`drawer-v2__tab${drawerTab === t.key ? ' drawer-v2__tab--active' : ''}`}
            onClick={() => { setDrawerTab(t.key); if (t.key === 'friends') setSocialBadge(0); }}
          >
            {t.label}
          </button>
        ))}
      </div>
      {drawerTab === 'reactions' ? (
        <ReactionsPanel />
      ) : drawerTab === 'achievements' ? (
        <AchievementsPanel />
      ) : (
        <SocialPanel
          onJoinGroup={(slug) => { setDrawerOpen(false); joinGroup(slug, user?.name ?? ''); }}
          onJoinRoom={(code) => { setDrawerOpen(false); joinRoom(code, user?.name ?? ''); }}
          onSpectateRoom={(code) => { setDrawerOpen(false); spectateRoom(code, user?.name ?? '', true); }}
          currentRoomCode={roomCode}
        />
      )}
    </Drawer>
  );

  // Session replaced by another tab
  if (sessionReplaced) {
    return (
      <div className="hero-panel">
        <div className="screen screen-center session-replaced">
          <h1 className="title-logo" style={{ fontSize: '2rem' }}>FLIP 7</h1>
          <div className="ornate-border">
            <p className="session-replaced__title">Session active in another tab</p>
            <p className="session-replaced__desc">
              This session has been taken over by another browser tab.
            </p>
          </div>
          <button
            className="btn btn-primary"
            onClick={() => reconnect()}
          >
            Reconnect
          </button>
        </div>
      </div>
    );
  }

  // No game state yet — show home, story, or daily challenge screen
  if (!gameState) {
    if (showDailyChallenge) {
      return (
        <>
          <ConnectionBanner />
          {drawerTrigger}
          {drawerContent}
          <DailyChallengeScreen
            onStartDaily={startDailyChallenge}
            onBack={() => setShowDailyChallenge(false)}
            dailyResult={dailyResult}
          />
        </>
      );
    }

    if (showStoryMode) {
      return (
        <>
          <ConnectionBanner />
          {drawerTrigger}
          {drawerContent}
          <StoryScreen
            onStartChallenge={startChallenge}
            onBack={() => setShowStoryMode(false)}
            challengeResult={challengeResult}
          />
        </>
      );
    }

    if (showShop) {
      return (
        <>
          <ConnectionBanner />
          {drawerTrigger}
          {drawerContent}
          <ShopScreen onBack={() => setShowShop(false)} />
        </>
      );
    }

    if (showProfile) {
      return (
        <>
          <ConnectionBanner />
          {drawerTrigger}
          {drawerContent}
          <ProfileScreen onBack={() => setShowProfile(false)} />
        </>
      );
    }

    if (showCollection) {
      return (
        <>
          <ConnectionBanner />
          {drawerTrigger}
          {drawerContent}
          <ShinyCollectionScreen onBack={() => setShowCollection(false)} />
        </>
      );
    }

    if (showDuelPub) {
      return (
        <>
          <ConnectionBanner />
          {drawerTrigger}
          {drawerContent}
          <DuelPubScreen onBack={() => setShowDuelPub(false)} />
        </>
      );
    }

    return (
      <>
        <ConnectionBanner />
        {drawerTrigger}
        {drawerContent}
        <HomeScreen
          onCreateRoom={createRoom}
          onJoinRoom={joinRoom}
          onSpectateRoom={spectateRoom}
          onJoinGroup={joinGroup}
          onStoryMode={() => { setShowStoryMode(true); window.history.pushState({ screen: 'story' }, ''); }}
          onDailyChallenge={() => { setShowDailyChallenge(true); window.history.pushState({ screen: 'daily' }, ''); }}
          onShop={() => { setShowShop(true); window.history.pushState({ screen: 'shop' }, ''); }}
          onProfile={() => { setShowProfile(true); window.history.pushState({ screen: 'profile' }, ''); }}
          onCollection={() => { setShowCollection(true); window.history.pushState({ screen: 'collection' }, ''); }}
          onDuelPub={() => { setShowDuelPub(true); window.history.pushState({ screen: 'duel' }, ''); }}
          onFriends={openFriends}
          error={error}
          initialRoomCode={urlRoomCode}
          groupSlug={urlGroupSlug}
        />
      </>
    );
  }

  const leaveButton = (
    <button className="leave-btn" onClick={handleLeave} aria-label="Leave game">
      <span className="leave-btn__arrow">&larr;</span>
      Menu
    </button>
  );

  // Render screen content based on game status
  function renderScreen() {
    switch (gameState!.status) {
      case 'waiting':
        // Hot Streak gets its own dedicated lobby
        if (isHotStreak) {
          return (
            <HotStreakLobbyScreen
              gameState={gameState!}
              playerId={playerId}
              isHost={isHost}
              onStartGame={startGame}
              onLeave={handleLeave}
            />
          );
        }
        return (
          <LobbyScreen
            gameState={gameState!}
            playerId={playerId}
            isHost={isHost}
            isSpectator={isSpectator}
            onStartGame={startGame}
            onAddPlayer={addPlayer}
            onRemovePlayer={removePlayer}
            onKickSpectator={kickSpectator}
            onToggleWantToPlay={toggleWantToPlay}
          />
        );

      case 'playing':
        return (
          <>
            {showCeremony && (
              <GameStartScreen
                gameState={gameState!}
                onComplete={() => setShowCeremony(false)}
              />
            )}
            <GameBoardScreen
              gameState={gameState!}
              playerId={playerId}
              isHost={isHost}
              isSpectator={isSpectator}
              onAddCard={addCard}
              onRemoveCard={removeCard}
              onStay={stay}
              onBust={bust}
              onNextPlayer={nextPlayer}
              onUndo={undo}
              onEndRound={endRound}
              onHit={hit}
              onChooseTarget={chooseTarget}
              onSelectSwapCards={selectSwapCards}
              onSelectStealTarget={selectStealTarget}
              onSelectDiscardTarget={selectDiscardTarget}
              onForfeitAction={forfeitAction}
              onKickPlayer={removePlayer}
              reactions={reactions}
              onLeave={handleLeave}
              chat={chat}
              onOpenDrawer={socialBadge > 0 ? openFriends : openDrawer}
              userAvatar={user?.activeAvatar ?? undefined}
              userName={user?.name}
              socialBadge={socialBadge}
            />
          </>
        );

      case 'round-summary':
        return (
          <RoundSummaryScreen
            gameState={gameState!}
            isHost={isHost}
            onNextRound={nextRound}
          />
        );

      case 'finished':
        return (
          <GameOverScreen
            gameState={gameState!}
            isHost={isHost}
            isSpectator={isSpectator}
            onPlayAgain={playAgain}
            onToggleWantToPlay={toggleWantToPlay}
            spectatorId={spectatorId}
          />
        );

      default:
        return (
          <div className="screen screen-center">
            <p>Something went wrong.</p>
            <button className="btn btn-primary" onClick={leaveRoom}>Return to Home</button>
          </div>
        );
    }
  }

  // Board screen owns its own header, chat, and mute — hide App-level versions
  const isBoardActive = gameState.status === 'playing';

  const isDuel = gameState.mode === 'duel';
  const isHotStreak = gameState.mode === 'hot-streak';

  // Hot Streak mode: render dedicated game screen when playing
  if (isHotStreak && gameState.status === 'playing' && roomCode) {
    return (
      <div className="hot-streak-arena">
        <ConnectionBanner />
        <HotStreakGameScreen roomCode={roomCode} playerId={playerId} reactions={reactions} />
      </div>
    );
  }

  return (
    <div className={isDuel ? 'duel-arena' : undefined}>
      <ConnectionBanner />
      {!isBoardActive && drawerTrigger}
      {drawerContent}
      {!isBoardActive && leaveButton}
      {isSpectator && (
        <div className="spectator-bar">
          <span className="spectator-badge">
            Spectating
            {(() => {
              const queued = gameState.spectators
                .filter(s => s.wantsToPlay)
                .sort((a, b) => (a.queuedAt ?? 0) - (b.queuedAt ?? 0));
              const pos = queued.findIndex(s => s.id === spectatorId) + 1;
              return pos > 0 ? ` · Queue #${pos}` : '';
            })()}
          </span>
          <button className="spectator-tv-btn" onClick={() => setTvMode(true)}>
            TV Mode
          </button>
        </div>
      )}
      <ScreenTransition screenKey={gameState.status}>
        {renderScreen()}
      </ScreenTransition>
      {!isBoardActive && (
        <ChatPanel
          roomMessages={chat.roomMessages}
          spectatorMessages={chat.spectatorMessages}
          unreadRoom={chat.unreadRoom}
          unreadSpectator={chat.unreadSpectator}
          isSpectator={isSpectator}
          onSend={chat.sendChat}
          onClearUnread={chat.clearUnread}
        />
      )}
      {!isBoardActive && <MuteToggle />}
      <ConfirmModal
        open={leaveConfirmOpen}
        title="Leave Game"
        message="Leave this game?"
        confirmLabel="Leave"
        variant="danger"
        onConfirm={confirmLeave}
        onCancel={() => setLeaveConfirmOpen(false)}
      />
      {tvMode && (
        <TVModeScreen
          gameState={gameState}
          onExitTV={() => setTvMode(false)}
        />
      )}
      {shinyUnlock && (
        <ShinyUnlockToast
          cardKey={shinyUnlock.cardKey}
          tier={shinyUnlock.tier as import('@flip7/shared').ShinyTier}
          onComplete={() => setShinyUnlock(null)}
        />
      )}
    </div>
  );
}
